<template>
  <modal-form-edit
    ref="editStatusModal"
    title="Status"
    form-component="status-form"
  >
    <app-loading-section
      :loading="isLoading"
    >
      <div class="subtitle mt-150"><icon-default icon="automation" size="is-small"></icon-default> Status Automations</div>

      <div v-if="hasStatusAutomationIntegration" class="is-relative" style="min-height: 100px">
        <status-collapse-section v-model="isAddAutomationOpen" primary>
          <template v-slot:label>
            <icon-default icon="plus" size="is-small" alignment="bottom"></icon-default> Add New Automation
          </template>
          <status-add-automation :status="status" @created="addAutomation"></status-add-automation>
        </status-collapse-section>

        <status-collapse-section
          v-for="item in computedAutomations"
          v-model="item.isOpen"
          :key="item.clientId"
        >
          <template v-slot:label>
            <icon-default :icon="item.icon" size="is-small" alignment="bottom"></icon-default> {{ item.label }}
            <span class="small-text">({{ item.hookLabel }})</span>
          </template>
          <status-add-automation :status="status" :automation="item" @updated="updateAutomation" @deleted="deleteAutomation"></status-add-automation>
        </status-collapse-section>
      </div>

      <div v-else class="mt-400">
        <app-upgrade-required is-centered is-large/>
      </div>
    </app-loading-section>
  </modal-form-edit>
</template>

<script>
import StatusAddAutomation from "./StatusAddAutomation";
import StatusCollapseSection from "./StatusCollapseSection";
import AppUpgradeRequired from "../../../../components/app/AppUpgradeRequired.vue";

export default {
  name: "StatusEditModal",
  components: {
    AppUpgradeRequired,
    StatusCollapseSection,
    StatusAddAutomation,
  },
  data() {
    return {
      isLoading: true,
      isSaving: false,
      isDeleting: false,
      status: {},
      statusForm: {},
      automations: [],
      requiresDate: false,
      validationErrors: {},
      isAddAutomationOpen: false
    }
  },
  mounted() {
    if (this.status.acceptable_time === null) {
      this.requiresDate = true
    }

    this.initForm();
  },
  computed: {
    hasStatusAutomationIntegration() {
      return this.$store.state.accounts.features.Automation;
    },
    acceptableTime: {
      get() {
        return this.statusForm.acceptable_time / 60;
      },
      set(newValue) {
        this.statusForm.acceptable_time = newValue * 60;
      }
    },
    typeIsNew() {
      return this.status.type === 2;
    },
    computedAutomations() {
      const automations = this.$utils.clone(this.automations);

      return automations.map(a => {
        const className = a.class_name.substring(11, a.length)

        switch(className) {
          case 'AddTaskAutomation':
            a.label = 'Add Task';
            a.icon = 'task';
            a.taskTemplates = a.taskTemplates.map(task => {
              task.validationErrors = {};
              task.isOpen = false;
              task.clientId = this.$utils.generateClientId();
              return task;
            });
            break;
          case 'NotifyUsersAutomation':
            a.label = 'Notify Users';
            a.icon = 'notifications';
            break;
          case 'ManageUsersAutomation':
            a.label = 'Manage Users';
            a.icon = 'users';
            break;
          case 'UpdateRecordTemperatureAutomation':
            a.label = 'Update Lead Temperature';
            a.icon = 'temperature';
            break;
          case 'SendWebhookAutomation':
            a.label = 'Send Webhook';
            a.icon = 'webhook';
            break;
          case 'EmailAutomation':
            a.label = 'Email';
            a.icon = 'email';
            break;
          case 'TextAutomation':
            a.label = 'Text';
            a.icon = 'text';
            break;
          case 'AddContactTagAutomation':
            a.label = 'Attach Tag';
            a.icon = 'tag';
            break;
          case 'AddToFollowUpSequenceAutomation':
            a.label = 'Add To Follow Up Sequence';
            a.icon = 'follow-up';
            break;
        }

        if (a.hook === 3) {
          if (a.minutes < 60) {
            a.hookLabel = `${(a.minutes)} Minutes Before Status Due`;
          } else {
            a.hookLabel = `${(a.minutes / 60).toFixed(2)} Hours Before Status Due`;
          }
        } else if (a.hook === 5) {
          if (a.minutes < 60) {
            a.hookLabel = `${(a.minutes)} Minutes After Enter Status`;
          } else {
            a.hookLabel = `${(a.minutes / 60).toFixed(2)} Hours After Enter Status`;
          }
        } else {
          a.hookLabel = this.$utils.getEnumLabelDeprecated('automationHookEnum', a.hook);
        }

        return a;
      }).sort((a, b) => a.label.localeCompare(b.label))
    }
  },
  methods: {
    open(status) {
      this.status = status;

      this.initForm();

      this.$refs.editStatusModal.open(this.statusForm);
      this.getAutomations();
    },
    close() {
      this.$refs.editStatusModal.close()
    },
    initForm() {
      const status = this.$utils.clone(this.status);

      this.statusForm = {
        id: status.id,
        state: status.state,
        order: status.order,
        label: status.label,
        acceptable_time: status.acceptable_time,
        pipeline_view_ids: status.pipeline_view_ids
      }
    },
    getAutomations() {
      const automations = [
        'AddTaskAutomation',
        'NotifyUsersAutomation',
        'ManageUsersAutomation',
        'UpdateRecordTemperatureAutomation',
        'SendWebhookAutomation',
        'EmailAutomation',
        'TextAutomation',
        'AddContactTagAutomation',
        'AddToFollowUpSequenceAutomation'
      ];

      const statusId = this.status.id;

      const getters = automations.map(a => {
        return this.$api.automations[a].all().then(res => {
          const thisStatusAutomations = res.data.filter(t => t.status_id === statusId);
          this.automations = [...this.automations, ...thisStatusAutomations];
        });
      });

      this.isLoading = true;
      this.automations = [];

      Promise.all(getters).then(() => {
        this.automations = this.automations.map(a => {
          a.isOpen = false;
          a.clientId = this.$utils.generateClientId();
          return a;
        });

        this.isLoading = false;
      });
    },
    statusOrderOptions(s) {
      if (s.order === status.order) {
        return 'Current Position';
      }
      else if (s.order > status.order) {
        return "After " + s.label;
      }
      else if (s.order < status.order) {
        return "Before " + s.label;
      }
    },
    addAutomation(item) {
      this.isAddAutomationOpen = false;

      setTimeout(() => {
        this.automations.push(item);
      }, 300);
    },
    updateAutomation(item) {
      this.automations = this.automations.map(a => (a.clientId === item.clientId) ? {...a, ...item} : a);
    },
    deleteAutomation(item) {
      this.automations = this.automations.filter(a => a.clientId !== item.clientId);
    },
    save() {
      let data = {
        id: this.status.id,
        order: this.statusForm.order,
        label: this.statusForm.label,
        acceptable_time: this.requiresDate ? null : this.statusForm.acceptable_time,
      };

      this.isSaving = true;

      this.$api.statuses.update(data).then(() => {
        this.$buefy.toast.open({message: "Updated Status", type: 'is-success'});
      }, (error) => {
        if (error.response.status === 422) { this.validationErrors = error.response.data.errors }
      }).finally(() => {
        this.isSaving = false;
      });
    },
    remove() {
      this.$utils.showDeleteDialog(this.status.label, () => {
        this.isDeleting = true;
        this.$api.statuses.delete(this.status.id);
        this.close();
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.feature_list {
  padding-left: 30px;
  list-style: disc;

  li {
    line-height: 1.3;
    margin-bottom: 4px;
  }
}

p {
  line-height: 1.3;
}
</style>
